import { randomUUID } from 'base/uuid';
import { recordMap } from 'blocks/woot_woot/string_map';
import { UndoManager } from 'blocks/woot_woot/undo';
import { computedFn } from 'mobx-utils';
import {
  aReferenceSection,
  Page,
  PageState,
  Project,
  ProjectState,
  Section,
  SectionRefState,
  SectionState,
  Space,
  SpaceState,
} from '../model';

export function createGlobal(
  section: SectionState,
  undo: UndoManager,
  project: Project,
): SectionRefState {
  const id = randomUUID().replaceAll('-', '');
  addGlobalSection(id, section, project, undo);
  return aReferenceSection({ id });
}

export function addGlobalSection(
  id: string,
  section: SectionState,
  project: Project,
  undo: UndoManager,
) {
  // We never undo the creation of a global.
  // The AI undo stack (& collaboration if we build that) effectively
  // reorder deltas.  So a global section might be referenced before
  // it is created.
  // Therefore, we hide unreferenced globals from the UI.
  undo.doPermanently(() => {
    project.globalSections.set(id, section);
  });
}

function counterAddAll(
  counter: Map<string, number>,
  other: ReadonlyMap<string, number>,
) {
  for (const [key, value] of other) {
    counter.set(key, (counter.get(key) ?? 0) + value);
  }
}

function counterInc(counter: Map<string, number>, key: string) {
  counter.set(key, (counter.get(key) ?? 0) + 1);
}

const referencedInPage = computedFn((page: Page | PageState) => {
  const out = new Map<string, number>();
  for (const section of page.sections) {
    if (section.type === 'reference') counterInc(out, section.id);
  }
  for (const sub of page.subPages) {
    counterAddAll(out, referencedInPage(sub));
  }
  return out;
});

const referencedInSpace = computedFn((space: Space | SpaceState) => {
  const out = new Map<string, number>();
  for (const page of space.subPages) {
    counterAddAll(out, referencedInPage(page));
  }
  return out;
});

const referencedInProject = computedFn((project: Project | ProjectState) => {
  const out = new Map<string, number>();
  counterAddAll(out, referencedInPage(project.homePage));
  for (const space of project.spaces) {
    counterAddAll(out, referencedInSpace(space));
  }
  return out;
});

export function removeUnreferencedGlobals(project: ProjectState): ProjectState {
  const referenced = referencedInProject(project);
  const out = new Map<string, SectionState>();
  for (const [id, section] of project.globalSections.entries()) {
    if (referenced.get(id)) out.set(id, section);
  }
  return { ...project, globalSections: recordMap(out) };
}

/** Excludes unreferenced ("deleted") globals. */
export const globalSectionsInProject = computedFn(
  (
    project: Project,
  ): ReadonlyMap<string, { value: Section; count: number }> => {
    const referenced = referencedInProject(project);
    const out = new Map<string, { value: Section; count: number }>();
    for (const [id, value] of project.globalSections.entries()) {
      const count = referenced.get(id);
      if (!count) continue;
      out.set(id, { value, count });
    }
    return out;
  },
);
